import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Box, Flex, Image, Text } from '@theme-ui/components';
import Cookies from 'js-cookie';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import Checkmark from '@app/components/Checkmark';
import ErrorBoundary from '@app/components/ErrorBoundary';
import { RetailerButton } from '@app/components/RetailerButton/RetailerButton';
import { H2 } from '@app/components/typography';
import { COUNTRY_COOKIE } from '@app/helpers/localization';
import { pushPerpetualEcommerceEvent } from '@app/helpers/tracking';
import useLocaleWithFallback from '@app/hooks/useLocaleWithFallback';
import StockMessage, {
  PACKAGE_TYPE,
} from '@app/pages/Pricing/components/StockMessage';
import colors from '@app/styles/theme/colors';
import { rem } from '@app/styles/theme/layout';

import type { PricingPage_content_pricing_pages_layout_item_pricing_pages_single_layout_product as DirectusProduct } from '../../__generated__/PricingPage';
import { isJPHardware } from '../../helpers';
import type { PricingPageShopProduct_magento2_products_items_Magento2_BundleProduct as BundleMagentoProduct } from '../../hook/__generated__/PricingPageShopProduct';
import useFreeShipping from '../../hook/useFreeShipping';
import AddToCartButton from '../AddToCartButton/AddToCartButton';
import AddToCartErrors from '../AddToCartErrors';
import Price from '../Price';
import messages from './ProductLayout.messages';
import { LayoutSubtitle } from './styles';

type BundleProductLayoutProps = {
  product: DirectusProduct;
  magentoProduct: BundleMagentoProduct;
  showRetailerButton: boolean | null;
};
const BundleProductLayout = ({
  product,
  magentoProduct,
  showRetailerButton,
}: BundleProductLayoutProps) => {
  const { findTranslationByKeyWithFallback } = useLocaleWithFallback();
  const { title } = product;
  const country = Cookies.get(COUNTRY_COOKIE);
  const { isFreeShipping } = useFreeShipping(country);
  const subtitle = findTranslationByKeyWithFallback({
    translations: product?.translations,
    key: 'subtitle',
  });

  useEffect(() => {
    const price = magentoProduct.priceRange.minimumPrice.finalPrice.value;
    const currency = magentoProduct.priceRange.minimumPrice.finalPrice.currency;

    pushPerpetualEcommerceEvent('view_item', {
      currency: currency || '',
      value: price || 0,
      items: [
        {
          item_id: product?.sku || '',
          item_name: product?.title || '',
          sdbs_id: magentoProduct?.sdbsId || '',
          sku_id: magentoProduct?.sku || '',
          sku_name: magentoProduct?.name || '',
          price: price || 0,
          quantity: 1,
        },
      ],
    });
  }, [magentoProduct, product]);

  return (
    <ErrorBoundary>
      <Box mb={5}>
        <AddToCartErrors />
        <Box sx={{ width: '100%' }} mb={5}>
          <H2 as="h1" data-testid="pricing-product-layout-title">
            {title}
          </H2>
          <LayoutSubtitle>{subtitle}</LayoutSubtitle>
        </Box>
        <Flex sx={{ flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <Box sx={{ width: ['100%', '33.3%'] }} mb={[5, 0]}>
            <Image
              sx={{ maxWidth: '100%' }}
              data-testid="pricing-product-layout-image"
              src={magentoProduct?.image?.url ?? undefined}
              alt={magentoProduct?.image?.label ?? undefined}
            />
          </Box>
          <Flex
            sx={{
              flexWrap: 'wrap',
              flexDirection: 'column',
              width: ['100%', '66.6%'],
            }}
            px={[0, 30, 50]}
          >
            <Box sx={{ width: ['100%', '100%', '50%'] }}>
              {magentoProduct && (
                <Box data-testid="product-bundle-info">
                  <Price price={magentoProduct.priceRange} />

                  {/* Stock of JP products is handled by MediaIntegration */}
                  {!isJPHardware(magentoProduct) && (
                    <StockIndicatorWrapper data-testid="product-bundle-info-message">
                      <StockMessage
                        packageType={PACKAGE_TYPE.DL}
                        stockStatus={magentoProduct.stockStatus}
                        releaseDate={magentoProduct.releaseDate}
                        freeShipping={isFreeShipping}
                      />
                    </StockIndicatorWrapper>
                  )}
                </Box>
              )}

              <Flex sx={{ justifyContent: 'flex-start' }}>
                <Flex
                  sx={{ flexDirection: 'column', flexGrow: ['1', 'unset'] }}
                >
                  <Box mb={12}>
                    <AddToCartButton product={magentoProduct} />
                  </Box>

                  {showRetailerButton && <RetailerButton />}

                  {isJPHardware(magentoProduct) && (
                    <Flex py={3}>
                      <Checkmark
                        color="white"
                        css={css`
                          display: flex;
                          flex: 1;
                          min-width: 24px;
                          height: 24px;
                          background-color: ${colors.acidGreen};
                          border-radius: 99999px;
                          margin-right: 12px;
                          &:after {
                            top: 3px;
                            left: 7px;
                          }
                        `}
                      />
                      <Text
                        sx={{
                          fontWeight: '500',
                          fontSize: rem(12),
                          lineHeight: '1.6',
                        }}
                      >
                        <FormattedMessage {...messages.mediaIntegrtion} />
                      </Text>
                    </Flex>
                  )}
                </Flex>
              </Flex>
            </Box>
          </Flex>
        </Flex>
      </Box>
    </ErrorBoundary>
  );
};

export const StockIndicatorWrapper = styled(Box)`
  margin-top: ${rem(15)};
  margin-bottom: ${rem(30)};
  font-size: ${rem(15)};
`;

export default BundleProductLayout;
